import React, {CSSProperties} from 'react'
import {useImageIsLoading} from './hooks'

interface SquareImageProps {
  /** URL of the image to be displayed. */
  src: string

  /** alternate text for the image */
  alt?: string

  /** Specific style properties to be applied. */
  style?: React.CSSProperties

  /** Specific content style properties to be applied. */
  contentStyle?: React.CSSProperties

  /** Fade the image when it loads, rather than popping or scanning */
  fadeOnLoad?: boolean
}

const defaultContentStyle: CSSProperties = {
  position: 'absolute',
  width: '100%',
  height: '100%',
  overflow: 'hidden'
}

const defaultImageContainerStyle: CSSProperties = {
  position: 'relative',
  width: '100%'
}

const getImgStyle = (src: string) =>
  ({
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    backgroundPosition: 'left top',
    backgroundImage: `url(${src})`
  }) as CSSProperties

/**
 * An image component that will automatically crop an image to be square,
 * and fit it inside the provided space.
 */
export function SquareImage({src, alt, style, contentStyle, fadeOnLoad}: SquareImageProps) {
  const isLoading = useImageIsLoading(src)

  const imageContainerStyle = React.useMemo(
    () => ({
      opacity: fadeOnLoad && isLoading ? 0 : 1,
      ...defaultImageContainerStyle,
      ...style
    }),
    [style, fadeOnLoad, isLoading]
  )

  const combinedContentStyle = React.useMemo(() => ({...defaultContentStyle, ...contentStyle}), [contentStyle])

  const imgStyle = React.useMemo(() => getImgStyle(src), [src])
  const classNames = React.useMemo(() => {
    if (fadeOnLoad) {
      return 'transition-opacity square-image-container'
    } else {
      return 'square-image-container'
    }
  }, [fadeOnLoad])

  return (
    <div className={classNames} style={imageContainerStyle}>
      <div style={combinedContentStyle}>
        <div style={imgStyle} aria-label={alt} />
      </div>
    </div>
  )
}
